import { z } from 'zod';

import { MIN_PASSWORD_LENGTH, type Permissions, type Role } from '../domain.js';

/**
 * A senha que a pessoa escolhe.
 *
 * O comprimento é só o piso — a regra que sustenta é a recusa de senha vazada,
 * conferida no servidor contra o Have I Been Pwned. Não exigimos maiúscula,
 * número e símbolo: essa combinação produz "Senha@2024" em toda repartição, que
 * está em todas as listas, enquanto reprova uma frase longa que não está em
 * nenhuma.
 */
export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `A senha precisa de ao menos ${MIN_PASSWORD_LENGTH} caracteres`)
  .max(200, 'Senha longa demais');

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('E-mail inválido'))
  .describe('E-mail');

/** Identificador do ambiente da plataforma na seleção de login. */
export const PLATFORM_ENVIRONMENT_ID = 'plataforma';

export const loginBodySchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Informe a senha'),
  /**
   * Ambiente escolhido: `plataforma` ou o id de uma organização.
   *
   * Ausente quando há um só ambiente possível. Havendo mais de um, o servidor
   * responde com a lista e o cliente repete o login com a escolha — não dá para
   * oferecer a lista antes de saber quem é a pessoa.
   */
  environmentId: z.string().optional(),
});
export type LoginBody = z.infer<typeof loginBodySchema>;

export const forgotPasswordBodySchema = z.object({ email: emailSchema });
export type ForgotPasswordBody = z.infer<typeof forgotPasswordBodySchema>;

export const resetPasswordBodySchema = z.object({
  token: z.string().min(16),
  password: passwordSchema,
});
export type ResetPasswordBody = z.infer<typeof resetPasswordBodySchema>;

/**
 * Troca de senha de quem está logado.
 *
 * `currentPassword` é opcional por um caso só: a troca obrigatória do primeiro
 * acesso. Ali a pessoa acabou de apresentar a senha atual no login que gerou
 * esta sessão, e pedir de novo a mesma senha padrão que o gestor ditou não
 * confere nada — só dá chance de errar. Fora desse caso o servidor exige.
 */
export const changePasswordBodySchema = z.object({
  currentPassword: z.string().min(1).optional(),
  newPassword: passwordSchema,
});
export type ChangePasswordBody = z.infer<typeof changePasswordBodySchema>;

// ─────────────────────────────────────────────────────── respostas

export interface AuthOrganization {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly defaultVisitIntervalDays: number;
  /**
   * O fuso do órgão, para a interface formatar data e hora nele.
   *
   * Vem na sessão e não numa consulta à parte porque toda tela precisa dele
   * antes de desenhar a primeira data — buscá-lo depois faria a lista de obras
   * aparecer uma vez no fuso errado.
   */
  readonly timezone: string;
}

export interface AuthUser {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly avatarUrl: string | null;
  readonly role: Role;
  readonly jobTitle: string | null;
}

/** Uma opção da seleção de ambiente no login. */
export interface AuthEnvironment {
  /** `plataforma` ou o id da organização. */
  readonly id: string;
  readonly kind: 'platform' | 'organization';
  readonly name: string;
}

export interface SessionResponse {
  /** JWT curto. Fica só em memória no cliente — nunca em localStorage. */
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly user: AuthUser;
  /** `null` no ambiente da plataforma, que não pertence a organização alguma. */
  readonly organization: AuthOrganization | null;
  /** O que a pessoa alcança neste órgão. Tudo falso no ambiente da plataforma. */
  readonly permissions: Permissions;
  readonly isPlatformAdmin: boolean;
  /** Ambientes disponíveis, para trocar sem sair do sistema. */
  readonly environments: readonly AuthEnvironment[];
  /**
   * A conta ainda está com a senha padrão e precisa trocá-la agora.
   *
   * Enquanto for `true` o servidor recusa qualquer rota que não seja a troca —
   * a tela que bloqueia é conveniência, não a trava.
   */
  readonly mustChangePassword: boolean;
}

/**
 * Resposta do login quando há mais de um ambiente possível.
 *
 * Sem token: a pessoa foi reconhecida, mas ainda não escolheu onde entrar.
 */
export interface EnvironmentChoice {
  readonly needsEnvironment: true;
  readonly environments: readonly AuthEnvironment[];
}

export type LoginResponse = SessionResponse | EnvironmentChoice;

export function needsEnvironment(response: LoginResponse): response is EnvironmentChoice {
  return 'needsEnvironment' in response;
}
